/**
 * App chrome: brand, primary navigation, account switcher, sign out.
 *
 * Navigation only advertises the kinds that were actually ingested. Showing a
 * "Movies" tab that leads to an empty grid is the exact confusion the opt-in
 * ingest model exists to avoid.
 */

import type { ReactNode } from 'react';
import { useAccounts } from '../lib/accounts-store';
import { useRecentErrors } from '../hooks/useRecentErrors';
import { useSession } from '../lib/session';
import { Link, useLocation } from '../router/router';
import { ErrorBadge } from './ErrorBadge';
import { Icon, type IconName } from './Icon';
import { ViewerBadge } from './ViewerBadge';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
}

export function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { accounts, active, setActive } = useAccounts();
  const { user, signOut } = useSession();
  const isAdmin = user?.role === 'admin';
  const { recentCount } = useRecentErrors(isAdmin);

  const items: NavItem[] = [{ to: '/', label: 'Library', icon: 'home' }];
  if (active?.ingest.live) items.push({ to: '/live', label: 'Live', icon: 'tv' });
  if (active?.ingest.movies) items.push({ to: '/movies', label: 'Movies', icon: 'film' });
  if (active?.ingest.series) items.push({ to: '/series', label: 'Series', icon: 'stack' });
  // Not tied to any account -- there is no panel login behind it, so it shows
  // up whether or not an Xtream account exists at all.
  items.push({ to: '/free', label: 'Free TV', icon: 'globe' });
  items.push({ to: '/accounts', label: 'Playlists', icon: 'sliders' });
  if (isAdmin) items.push({ to: '/people', label: 'People', icon: 'people' });
  if (isAdmin) items.push({ to: '/settings', label: 'Settings', icon: 'settings' });

  return (
    <div className="shell">
      <header className="shell__header">
        <Link to="/" className="shell__brand">
          <Icon name="tv" size={20} />
          <span>SFLX Live</span>
        </Link>

        <nav className="shell__nav" aria-label="Primary">
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="navlink"
              aria-current={isCurrent(pathname, item.to) ? 'page' : undefined}
            >
              <Icon name={item.icon} size={16} />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="shell__spacer" />

        {accounts.length > 1 ? (
          <label className="row" style={{ gap: 'var(--s-2)' }}>
            <span className="sr-only">Active account</span>
            <select
              className="select"
              style={{ width: 'auto', maxWidth: 180 }}
              value={active?.id ?? ''}
              onChange={(event) => setActive(event.target.value)}
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <ViewerBadge enabled={isAdmin} />
        <ErrorBadge count={recentCount} />

        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void signOut()}
          title={user ? `Signed in as ${user.email}` : 'Sign out'}
        >
          <Icon name="signOut" size={16} />
          <span className="sr-only">Sign out</span>
        </button>
      </header>

      <main className="shell__main">{children}</main>
    </div>
  );
}

function isCurrent(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}
